import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { App } from '@/screens/pt/App'
import { ApiError } from '@/data/api'
import { toast, TooltipProvider } from '@/ui'
import './styles/index.css'
import './styles/global.css'

/* Экспортируется, чтобы сбрасывать кеш можно было и не из React —
   например из слоя входа при смене учётной записи. */
export const queryClient = new QueryClient({
  /* Одна воронка отказов на все команды. Доменная причина приходит в поле
     code, и показывать её обязан сервер, а не каждый экран своими словами:
     иначе один и тот же отказ будет называться по-разному в трёх местах. */
  mutationCache: new MutationCache({
    onError: (error) => {
      const problem = error instanceof ApiError ? error : null
      toast({ tone: 'danger', title: problem?.title ?? 'Команда не выполнена', text: problem?.detail ?? String(error) })
    },
  }),
  defaultOptions: {
    queries: {
      /* Ответ 4xx — это решение сервера, а не сбой связи: повтор даст тот же
         ответ и только задержит экран. Особенно заметно на 404: карточка
         документа спрашивает его в двух реестрах сразу, и промах в одном —
         норма, а не ошибка. Повторяются только 5xx и обрывы сети. */
      retry: (attempt, error) => {
        if (error instanceof ApiError && error.status < 500) return false
        return attempt < 2
      },
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
    // Команду повторять нельзя вслепую: тот же ключ идемпотентности вернёт
    // прежний результат, а другой — выполнит операцию второй раз.
    mutations: { retry: false },
  },
})

const rootEl = document.getElementById('root')
if (!rootEl) throw new Error('#root not found')

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      {/* Адрес — часть состояния приложения: экран, выбранная запись и
          открытая карточка читаются из него. Отдача index.html на любой путь
          уже настроена в nginx (try_files), поэтому прямая ссылка работает и
          в продакшене, а не только в dev-сервере. */}
      {/* Provider у подсказок один на приложение. Свой на каждую подсказку
          означал, что пропуск задержки между соседними не работает: наведя на
          вторую кнопку панели, ждёшь те же 300 мс заново. */}
      <TooltipProvider delayDuration={300} skipDelayDuration={300}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </StrictMode>,
)
